import { Injectable, signal, computed } from '@angular/core';
import { ApiService } from '../API/api-service';
import { AuthService } from '../Auth/auth-service';
import { Observable, tap, catchError, of } from 'rxjs';
import { ThemeService } from './theme.service';

export interface UserTheme {
  themePath: string;
  isActive: boolean;
  userId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeManagementService {
  // Signals for reactive state management
  private readonly _currentTheme = signal<UserTheme | null>(null);
  private readonly _isLoading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  // Computed values for components
  readonly currentTheme = computed(() => this._currentTheme());
  readonly isLoading = computed(() => this._isLoading());
  readonly error = computed(() => this._error());

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private themeService: ThemeService
  ) {
    // Load user theme on service initialization
    this.loadUserTheme();
  }

  /**
   * Load user theme from API or localStorage
   */
  private loadUserTheme(): void {
    // First try to get from localStorage for immediate response
    const storedTheme = this.getStoredTheme();
    if (storedTheme) {
      this._currentTheme.set(storedTheme);
    }

    // Then fetch from API to ensure consistency
    if (this.authService.isAuthenticated()) {
      // this.fetchUserThemeFromAPI();
    }
  }

  /**
   * Fetch user theme from API
   */
  // private fetchUserThemeFromAPI(): void {
  //   this._isLoading.set(true);
  //   this._error.set(null);

  //   this.apiService.getUserTheme().pipe(
  //     tap((response: any) => {
  //       if (response.success && response.data) {
  //         const themeData: UserTheme = {
  //           themePath: response.data.themePath || '',
  //           isActive: response.data.isActive || false,
  //           userId: response.data.userId
  //         };
  //         this._currentTheme.set(themeData);
  //         this.storeTheme(themeData);
  //       }
  //     }),
  //     catchError((error) => {
  //       console.error('Error fetching user theme:', error);
  //       this._error.set('Failed to load theme from server');
  //       return of(null);
  //     })
  //   ).subscribe(() => {
  //     this._isLoading.set(false);
  //   });
  // }

  /**
   * Save theme to API and local storage
   */
  saveUserTheme(themePath: string, isActive: boolean): Observable<any> {
    this._isLoading.set(true);
    this._error.set(null);

    const themeData: UserTheme = {
      themePath,
      isActive
    };

    // Update local state immediately for responsive UI
    this._currentTheme.set(themeData);
    this.storeTheme(themeData);

     // Get current user data
    const currentUser = this.authService.getUser();
    if (!currentUser?._id) {
      this._error.set('User not authenticated');
      this._isLoading.set(false);
      return of({ success: false, error: 'User not authenticated' });
    }

    const currentThemeMode = this.themeService.getTheme();

    // Prepare API payload with new format
    const apiPayload = {
      _id: currentUser._id,
      theme: themePath,
      mode: currentThemeMode
    };

    // Send to API
    return this.apiService.updateUserData(apiPayload).pipe(
      tap((response: any) => {
        const isSuccess = response.success === true || response.status === true;
        if (isSuccess) {
          // console.log('Theme saved successfully:', response);
          // Update with server response data if needed
          if (response.data) {
            this._currentTheme.set({
              ...themeData,
              userId: response.data.userId
            });
          }
        } else {
          this._error.set(response.message || 'Failed to save theme');
        }
      }),
      catchError((error) => {
        console.error('Error saving theme:', error);
        this._error.set('Failed to save theme to server');
        return of({ success: false, error });
      })
    );
  }

  /**
   * Get current theme path
   */
  getCurrentThemePath(): string {
    const theme = this._currentTheme();
    return theme?.themePath || '';
  }

  /**
   * Check if theme is active
   */
  isThemeActive(): boolean {
    const theme = this._currentTheme();
    return theme?.isActive || false;
  }

  /**
   * Store theme in localStorage
   */
  private storeTheme(theme: UserTheme): void {
    try {
      localStorage.setItem('userTheme', JSON.stringify(theme));
      localStorage.setItem('selectedBackgroundTheme', theme.themePath);
      localStorage.setItem('theme-background-active', theme.isActive.toString());
    } catch (error) {
      console.error('Error storing theme in localStorage:', error);
    }
  }

  /**
   * Get stored theme from localStorage
   */
  private getStoredTheme(): UserTheme | null {
    try {
      const stored = localStorage.getItem('userTheme');
      if (stored) {
        return JSON.parse(stored);
      }
      
      // Fallback to old localStorage format
      const themePath = localStorage.getItem('selectedBackgroundTheme');
      const isActive = localStorage.getItem('theme-background-active') === 'true';
      
      if (themePath || isActive) {
        return {
          themePath: themePath || '',
          isActive
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error reading stored theme:', error);
      return null;
    }
  }

  /**
   * Clear theme data
   */
  clearTheme(): void {
    this._currentTheme.set(null);
    localStorage.removeItem('userTheme');
    localStorage.removeItem('selectedBackgroundTheme');
    localStorage.removeItem('theme-background-active');
  }


   saveThemeMode(): Observable<any> {
    this._isLoading.set(true);
    this._error.set(null);

    // Get current user data
    const currentUser = this.authService.getUser();
    if (!currentUser?._id) {
      this._error.set('User not authenticated');
      this._isLoading.set(false);
      return of({ success: false, error: 'User not authenticated' });
    }

    // Get current theme mode (light/dark)
    const currentThemeMode = this.themeService.getTheme();
    
    // Get current theme path from localStorage
    const currentThemePath = localStorage.getItem('selectedBackgroundTheme') || '';
    
    // Prepare API payload
    const apiPayload = {
      _id: currentUser._id,
      theme: currentThemePath,
      mode: currentThemeMode
    };

    // Send to API
    return this.apiService.updateUserData(apiPayload).pipe(
      tap((response: any) => {
        const isSuccess = response.success === true || response.status === true;
        if (isSuccess) {
          // console.log('Theme mode saved successfully:', response);
        } else {
          this._error.set(response.message || 'Failed to save theme mode');
        }
      }),
      catchError((error) => {
        console.error('Error saving theme mode:', error);
        this._error.set('Failed to save theme mode to server');
        return of({ success: false, error });
      })
    );
  }

  /**
   * Reset error state
   */
  clearError(): void {
    this._error.set(null);
  }
} 